import React, { useState } from 'react';
import { NodePrediction } from '../services/api';
import { motion, AnimatePresence } from 'framer-motion';

interface PredictionCardProps {
  prediction: NodePrediction;
}

// Must match backend CLASS_NAMES dict exactly (index 0-6)
const CLASS_NAMES = [
  'Neural Networks',      // 0
  'Rule Learning',        // 1
  'Reinforcement Learning', // 2
  'Probabilistic Methods', // 3
  'Theory',               // 4
  'Genetic Algorithms',   // 5
  'Case Based',           // 6
];

const CLASS_COLORS = [
  '#6366f1', '#8b5cf6', '#22d3ee',
  '#10b981', '#f59e0b', '#f43f5e', '#a78bfa'
];

export const PredictionCard: React.FC<PredictionCardProps> = ({ prediction }) => {
  const [isExpanded, setIsExpanded] = useState(false);

  const confidenceLevel =
    prediction.confidence > 0.8 ? 'high' :
    prediction.confidence > 0.6 ? 'medium' : 'low';

  const confidencePct = (prediction.confidence * 100).toFixed(1);

  const confidenceColor =
    confidenceLevel === 'high' ? '#6bf7fb' :
    confidenceLevel === 'medium' ? '#00dddd' : '#ffb4ab';

  // SVG ring
  const radius = 20;
  const circumference = 2 * Math.PI * radius;
  const offset = circumference - prediction.confidence * circumference;

  const classColor = CLASS_COLORS[prediction.predicted_class % CLASS_COLORS.length];

  return (
    <motion.div
      className="prediction-card"
      style={{
        background: 'rgba(32, 31, 31, 0.7)',
        border: '1px solid rgba(131, 148, 147, 0.15)',
        borderRadius: '16px',
        padding: '1.25rem',
        position: 'relative',
        overflow: 'hidden',
        transition: 'all 0.25s ease',
      }}
      whileHover={{
        borderColor: confidenceLevel === 'high' ? 'rgba(107,247,251,0.35)' : 'rgba(148,163,184,0.25)',
        boxShadow: confidenceLevel === 'high' ? '0 0 20px rgba(107,247,251,0.1)' : '0 8px 24px rgba(0,0,0,0.3)',
      }}
      layout
    >
      {/* Top accent line */}
      <div style={{
        position: 'absolute', top: 0, left: 0, right: 0,
        height: '2px',
        background: `linear-gradient(90deg, ${classColor}88, ${classColor})`,
        borderRadius: '16px 16px 0 0',
      }} />

      {/* Header row: Node ID + confidence ring */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '1rem', marginTop: '4px' }}>
        {/* Node ID */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem' }}>
          <div style={{
            width: 32, height: 32, borderRadius: '10px',
            background: 'rgba(255,255,255,0.05)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: '0.9rem',
          }}>📄</div>
          <div>
            <div style={{ fontSize: '0.6rem', color: '#839493', textTransform: 'uppercase', letterSpacing: '0.08em', fontWeight: 600, marginBottom: 2 }}>
              Node ID
            </div>
            <div style={{ fontFamily: 'monospace', fontSize: '0.9rem', fontWeight: 700, color: '#ffffff' }}>
              #{prediction.node_id}
            </div>
          </div>
        </div>

        {/* Confidence ring */}
        <div style={{ position: 'relative', width: 48, height: 48, flexShrink: 0 }}>
          <svg viewBox="0 0 50 50" style={{ width: 48, height: 48, transform: 'rotate(-90deg)' }}>
            <circle cx="25" cy="25" r={radius} fill="none" stroke="rgba(148,163,184,0.1)" strokeWidth="4" />
            <motion.circle
              cx="25" cy="25" r={radius}
              fill="none"
              stroke={confidenceColor}
              strokeWidth="4"
              strokeLinecap="round"
              strokeDasharray={circumference}
              initial={{ strokeDashoffset: circumference }}
              animate={{ strokeDashoffset: offset }}
              transition={{ duration: 1.2, ease: 'easeOut' }}
            />
          </svg>
          <div style={{
            position: 'absolute', inset: 0,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontFamily: 'monospace', fontSize: '0.58rem', fontWeight: 700,
            color: confidenceColor,
          }}>
            {confidencePct}%
          </div>
        </div>
      </div>

      {/* Predicted class */}
      <div style={{ marginBottom: '1rem' }}>
        <div style={{ fontSize: '0.6rem', color: '#839493', textTransform: 'uppercase', letterSpacing: '0.08em', fontWeight: 600, marginBottom: 4 }}>
          Predicted Topic
        </div>
        <div style={{
          fontSize: '1rem', fontWeight: 700,
          color: classColor,
          lineHeight: 1.3,
        }}>
          {prediction.class_name}
        </div>
      </div>

      {/* Confidence badge */}
      <div style={{ marginBottom: '0.75rem' }}>
        <span style={{
          display: 'inline-flex', alignItems: 'center', gap: '0.3rem',
          padding: '0.2rem 0.6rem',
          borderRadius: '999px',
          fontSize: '0.65rem',
          fontWeight: 600,
          background: confidenceLevel === 'high' ? 'rgba(107,247,251,0.1)' : confidenceLevel === 'medium' ? 'rgba(0,221,221,0.1)' : 'rgba(255,180,171,0.1)',
          color: confidenceColor,
          border: `1px solid ${confidenceColor}33`,
        }}>
          <span style={{ width: 6, height: 6, borderRadius: '50%', background: confidenceColor, display: 'inline-block' }} />
          {confidenceLevel === 'high' ? 'High Confidence' : confidenceLevel === 'medium' ? 'Medium Confidence' : 'Low Confidence'}
        </span>
      </div>

      {/* Expand toggle */}
      <button
        style={{
          display: 'flex', alignItems: 'center', gap: '0.4rem',
          width: '100%', padding: '0.6rem 0 0',
          borderTop: '1px solid rgba(255,255,255,0.05)',
          background: 'none', border: 'none',
          cursor: 'pointer',
          color: '#839493', fontSize: '0.72rem', fontWeight: 600,
          transition: 'color 0.15s',
          fontFamily: 'inherit',
        }}
        onMouseEnter={e => (e.currentTarget.style.color = '#b9cac9')}
        onMouseLeave={e => (e.currentTarget.style.color = '#839493')}
        onClick={() => setIsExpanded(!isExpanded)}
      >
        <motion.span animate={{ rotate: isExpanded ? 180 : 0 }} transition={{ duration: 0.2 }}
          style={{ display: 'inline-block', fontSize: '0.6rem' }}>▼</motion.span>
        {isExpanded ? 'Hide' : 'Show'} Probability Breakdown
      </button>

      {/* Probability bars */}
      <AnimatePresence>
        {isExpanded && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.25 }}
            style={{ overflow: 'hidden' }}
          >
            <div style={{ paddingTop: '0.75rem', display: 'flex', flexDirection: 'column', gap: '6px' }}>
              {prediction.probabilities.map((prob, idx) => (
                <div key={idx} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  <span style={{
                    width: 80, fontSize: '0.62rem', color: '#b9cac9',
                    whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                    flexShrink: 0,
                  }}>
                    {CLASS_NAMES[idx] || `Class ${idx}`}
                  </span>
                  <div style={{
                    flex: 1, height: 5,
                    background: 'rgba(255,255,255,0.05)',
                    borderRadius: 4, overflow: 'hidden',
                  }}>
                    <motion.div
                      initial={{ width: 0 }}
                      animate={{ width: `${prob * 100}%` }}
                      transition={{ duration: 0.7, delay: idx * 0.04 }}
                      style={{
                        height: '100%',
                        borderRadius: 4,
                        background: idx === prediction.predicted_class
                          ? CLASS_COLORS[prediction.predicted_class % CLASS_COLORS.length]
                          : 'rgba(148,163,184,0.2)',
                      }}
                    />
                  </div>
                  <span style={{
                    width: 30, textAlign: 'right',
                    fontFamily: 'monospace', fontSize: '0.62rem', color: '#839493',
                    flexShrink: 0,
                  }}>
                    {(prob * 100).toFixed(0)}%
                  </span>
                </div>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
};
